"use client"

import { createContext, useContext, useState, useEffect } from "react"
import {
  clearActiveAuthSession,
  getAuthToken,
  getAuthUser,
  persistAuthSession,
  updateStoredAuthUser,
} from "../utils/authStorage"
import { API_BASE_URL, IS_LOCAL_API } from "../config/api"

const AuthContext = createContext()

export { AuthContext }

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}

const normalizeUser = (src) => {
  if (!src) return null
  return {
    ...src,
    id: src.id || src._id,
    _id: src._id || src.id,
    createdBy: src.createdBy,
    chatUserId: src.chatUserId || (src.role === "agent" ? src.createdBy?._id || src.createdBy : src.id || src._id),
    cloudNumber: src.cloudNumber || src.cloud_number || "",
    phoneNumberId: src.phoneNumberId || src.phone_number_id || "",
    label: src.label || "",
    wabaNumbers: src.wabaNumbers || [],
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [token, setToken] = useState(getAuthToken())
  const [loading, setLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [publicBranding, setPublicBranding] = useState(null)

  if (process.env.NODE_ENV !== "production") {
    console.log("API_BASE_URL:", API_BASE_URL)
  }

  // Fetch public white-label reseller branding matching hostname or IP
  useEffect(() => {
    const fetchPublicBranding = async () => {
      try {
        const host = window.location.hostname
        const response = await fetch(`${API_BASE_URL}/api/auth/public-branding?domain=${encodeURIComponent(host)}`)
        if (response.ok) {
          const data = await response.json()
          if (data.success && data.branding) {
            setPublicBranding(data.branding)
          }
        }
      } catch (err) {
        console.error("Error fetching public branding:", err)
      }
    }
    fetchPublicBranding()
  }, [])

  // Calculate effective logo URL & Brand Name based on role and reseller hierarchy
  const getRawLogo = () => {
    if (!user) {
      return publicBranding?.logo || ""
    }
    // Admin & Superadmin MUST ALWAYS see default DigiForest logo
    if (["admin", "superadmin"].includes(user.role)) {
      return ""
    }
    if (user.role === "reseller") {
      return user.resellerBranding?.logo || publicBranding?.logo || ""
    }
    // For regular users & agents: show parent reseller logo if available
    return user.parentResellerBranding?.logo || user.resellerBranding?.logo || publicBranding?.logo || ""
  }

  const rawLogo = getRawLogo()
  const effectiveLogo = rawLogo ? (rawLogo.startsWith("http") || rawLogo.startsWith("data:") ? rawLogo : `${API_BASE_URL.replace(/\/api$/, "")}${rawLogo}`) : ""

  const getEffectiveBrandName = () => {
    if (!user) {
      return publicBranding?.companyName || "DigiForest"
    }
    // Admin & Superadmin MUST ALWAYS have brand name "DigiForest"
    if (["admin", "superadmin"].includes(user.role)) {
      return "DigiForest"
    }
    if (user.role === "reseller") {
      return user.resellerBranding?.companyName || publicBranding?.companyName || "DigiForest"
    }
    return user.parentResellerBranding?.companyName || user.resellerBranding?.companyName || publicBranding?.companyName || "DigiForest"
  }

  const effectiveBrandName = getEffectiveBrandName()

  // Dynamically update browser tab title (header title)
  useEffect(() => {
    const titleText = effectiveBrandName && effectiveBrandName !== "DigiForest"
      ? `${effectiveBrandName} Dashboard`
      : "DigiForest Dashboard"
    document.title = titleText
  }, [effectiveBrandName, user])

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = getAuthToken()
      const storedUser = getAuthUser()

      if (storedToken && storedUser) {
        setToken(storedToken)
        setUser(storedUser)
        setIsAuthenticated(true)
      }
      setLoading(false)
    }

    initAuth()
  }, [])

  // API helper function
  const apiCall = async (endpoint, options = {}) => {
    const url = `${API_BASE_URL}${endpoint}`

    // TEMPORARY: Add demo token for testing media upload
    const tokenFromStorage = getAuthToken()
    const overrideToken = options.__tokenOverride
    if (overrideToken) delete options.__tokenOverride
    
    // For testing purposes, if no token exists, use a demo token
    let authToken = overrideToken || token || tokenFromStorage
    // if (!authToken && (endpoint.includes("/templatecreate/") || endpoint.includes("/upload"))) {
    //   authToken = "demo-token-12345"
    //   console.log("[v0] Using demo token for testing")
    // }

    // console.log(`[v0] API Call: ${options.method || "GET"} ${url}`)
    // console.log(`[v0] Token present: ${!!authToken}`)
    // console.log(`[v0] Token from storage: ${tokenFromStorage}`)
    // console.log(`[v0] Token from state: ${token}`)
    // console.log(`[v0] Final auth token: ${authToken}`)

    const isFormData = options?.body instanceof FormData
    const headers = {
      ...(options.headers || {}),
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    }

    console.log(`[v0] Request headers:`, headers)

    const config = {
  ...options,
  headers,
};

    if (config.body && !isFormData && typeof config.body === "object") {
      config.body = JSON.stringify(config.body)
    }

    try {
      const response = await fetch(url, config)

      console.log(`[v0] Response status: ${response.status}`)

      if (!response.ok) {
        const errorText = await response.text()
        console.error(`[v0] API Error Response:`, errorText)

        let errorData
        try {
          errorData = JSON.parse(errorText)
        } catch {
          errorData = { message: errorText || `HTTP ${response.status}` }
        }

        let err;
        if (response.status === 401) {
          // Token expired or invalid
          logout()
          err = new Error("Authentication failed. Please login again.")
        } else if (response.status === 400) {
          const errorMsg = errorData.error || errorData.message || "Bad request"
          if (typeof errorMsg === 'string' && (errorMsg.includes("Access denied") || errorMsg.includes("token"))) {
            err = new Error("Authentication failed - invalid or missing token")
          } else {
            err = new Error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg))
          }
        } else if (response.status === 403) {
          const errorMsg = errorData.error || errorData.message || "Access denied - insufficient permissions"
          if (typeof errorMsg === 'string' && errorMsg.toLowerCase().includes("deactivated")) {
            logout()
          }
          err = new Error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg))
        } else if (response.status === 404) {
          err = new Error("Resource not found")
        } else {
          const errorMsg = errorData.error || errorData.message || "API request failed"
          err = new Error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg))
        }
        err.details = errorData.details;
        throw err;
      }

      const data = await response.json()
      console.log(`[v0] API Success Response:`, data)
      return data
    } catch (error) {
      console.error(`[v0] API Call Failed:`, error)

      if (error.message.includes("fetch") || error.name === "TypeError" || error.message.includes("Failed to fetch")) {
        console.warn("[v0] Backend server not available, using demo mode")

        if (endpoint === "/api/auth/login" && options.method === "POST") {
          const loginPayload =
            typeof options.body === "string"
              ? JSON.parse(options.body)
              : typeof config.body === "string"
                ? JSON.parse(config.body)
                : options.body || config.body || {}
          const { email, password } = loginPayload
          if (email === "admin@example.com" && password === "admin123") {
            return {
              success: true,
              token: "demo-token-12345",
              user: {
                id: "demo-user-1",
                email: "admin@example.com",
                name: "Demo User",
                role: "admin",
              },
            }
          }
        }

        if (endpoint.startsWith("/api/chats/")) {
          return {
            success: true,
            data: [
              {
                conversationId: "demo-conv-1",
                contactName: "Demo Contact",
                contactNumber: "+1234567890",
                lastMessage: "Hello! This is a demo message.",
                lastMessageAt: new Date().toISOString(),
                unreadCount: 0,
              },
            ],
          }
        }

        if (endpoint.includes("/api/send-template")) {
          return {
            success: true,
            messageId: "demo-msg-" + Date.now(),
            status: "sent",
          }
        }
      }

      throw error
    }
  }

  // Authentication functions
  const login = async (email, password, captchaId, captchaValue) => {
    try {
      const data = await apiCall("/api/auth/login", {
        method: "POST",
        body: { email, password, captchaId, captchaValue },
      })

      if (data.requiresTwoFactor) {
        return {
          success: true,
          requiresTwoFactor: true,
          twoFactorToken: data.twoFactorToken,
          message: data.message || "Two-factor verification code sent",
        }
      }

      setToken(data.token)

      let me = null
      try {
        me = await apiCall("/api/auth/me", { __tokenOverride: data.token })
      } catch (e) {
        console.warn("[v0] /api/auth/me failed, using login payload only", e?.message)
      }

      const mergedUser = normalizeUser({ ...(data.user || {}), ...(me?.user || {}) })

      setUser(mergedUser)
      setIsAuthenticated(true)
      persistAuthSession(data.token, mergedUser)

      return { success: true, user: mergedUser }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const verifyTwoFactorLogin = async (twoFactorToken, code) => {
    try {
      const data = await apiCall("/api/auth/verify-two-factor", {
        method: "POST",
        body: { twoFactorToken, code },
      })

      setToken(data.token)

      let me = null
      try {
        me = await apiCall("/api/auth/me", { __tokenOverride: data.token })
      } catch (e) {
        console.warn("[v0] /api/auth/me failed after 2FA, using login payload only", e?.message)
      }

      const mergedUser = normalizeUser({ ...(data.user || {}), ...(me?.user || {}) })

      setUser(mergedUser)
      setIsAuthenticated(true)
      persistAuthSession(data.token, mergedUser)

      return { success: true, user: mergedUser }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const logout = () => {
    setToken(null)
    setUser(null)
    setIsAuthenticated(false)
    clearActiveAuthSession()
  }

  // Password reset functions
  const forgotPassword = async (email) => {
    try {
      const data = await apiCall("/api/auth/forgot-password", {
        method: "POST",
        body: { email },
      })
      return { success: true, resetToken: data.resetToken, message: data.message }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const verifyResetCode = async (resetToken, code) => {
    try {
      const data = await apiCall("/api/auth/verify-reset-code", {
        method: "POST",
        body: { resetToken, code },
      })
      return { success: true, message: data.message }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const resetPassword = async (resetToken, code, newPassword) => {
    try {
      const data = await apiCall("/api/auth/reset-password", {
        method: "POST",
        body: { resetToken, code, newPassword },
      })
      return { success: true, message: data.message }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  // User management functions
  const getAllUsers = async () => {
    try {
      const data = await apiCall("/api/auth/users")
      // Normalize users before returning
      const normalizedUsers = data.map(normalizeUser)
      return { success: true, users: normalizedUsers }
    } catch (error) {
      const fallbackUser = normalizeUser(user || getAuthUser())
      if (
        fallbackUser &&
        (error.message?.includes("fetch") ||
          error.message?.includes("Failed to fetch") ||
          error.message?.includes("Resource not found"))
      ) {
        console.warn("[v0] Falling back to active user for user list:", error.message)
        return { success: true, users: [fallbackUser], fallback: true }
      }
      return { success: false, error: error.message }
    }
  }

  const createUser = async (userData) => {
    try {
      const data = await apiCall("/api/auth/create-user", {
        method: "POST",
        body: userData,
      })
      // Normalize the created user
      const normalizedUser = normalizeUser(data.user)
      return { success: true, user: normalizedUser, message: data.message }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const editUser = async (userId, userData) => {
    try {
      const data = await apiCall(`/api/auth/users/${userId}`, {
        method: "PUT",
        body: userData,
      })
      // Normalize the updated user
      const normalizedUser = normalizeUser(data.updatedUser)
      return { success: true, user: normalizedUser, message: data.message }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const syncUserLimit = async (userId, wabaIndex = null) => {
    try {
      const data = await apiCall(`/api/auth/users/${userId}/sync-limit`, {
        method: "PUT",
        body: JSON.stringify({ wabaIndex }),
      })
      if (data && (data.success || data.wabaLimit)) {
        const syncedLimit = data.wabaLimit
        setUser((prev) => {
          if (!prev) return prev
          const isSelf = String(prev._id || prev.id) === String(userId)
          if (!isSelf) return prev
          const updated = {
            ...prev,
            wabaLimit: syncedLimit,
            limitUpdatedAt: data.limitUpdatedAt || new Date().toISOString()
          }
          persistAuthSession(getAuthToken(), updated)
          return updated
        })
      }
      return { success: true, ...data }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const deleteUser = async (userId) => {
    try {
      const data = await apiCall(`/api/auth/users/${userId}`, {
        method: "DELETE",
      })
      return { success: true, message: data.message }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const impersonateUser = async (userId) => {
    try {
      const data = await apiCall(`/api/auth/impersonate/${userId}`)

      // Store original user data for returning later
      if (typeof window !== "undefined") {
        window.sessionStorage.setItem("originalUser", JSON.stringify(user))
        window.sessionStorage.setItem("originalToken", token || getAuthToken())
      }

      const normalizedImpersonatedUser = normalizeUser(data.user)
      setToken(data.token)
      setUser({ ...normalizedImpersonatedUser, isImpersonating: true })
      persistAuthSession(data.token, { ...normalizedImpersonatedUser, isImpersonating: true })

      return { success: true, user: normalizedImpersonatedUser }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const stopImpersonation = () => {
    const originalUser =
      typeof window !== "undefined"
        ? window.sessionStorage.getItem("originalUser") || window.localStorage.getItem("originalUser")
        : null
    const originalToken =
      typeof window !== "undefined"
        ? window.sessionStorage.getItem("originalToken") || window.localStorage.getItem("originalToken")
        : null

    if (originalUser && originalToken) {
      const normalizedOriginalUser = normalizeUser(JSON.parse(originalUser))
      setUser(normalizedOriginalUser)
      setToken(originalToken)
      persistAuthSession(originalToken, normalizedOriginalUser)
      if (typeof window !== "undefined") {
        window.sessionStorage.removeItem("originalUser")
        window.sessionStorage.removeItem("originalToken")
        window.localStorage.removeItem("originalUser")
        window.localStorage.removeItem("originalToken")
      }
    }
  }

  // Balance management functions
  const creditUserBalance = async (userId, balance, expiry, description) => {
    try {
      const data = await apiCall(`/api/auth/credit/${userId}`, {
        method: "PUT",
        body: { balance, expiry, description },
      })
      return { success: true, message: data.message, transaction: data.transaction }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const getUserBalance = async () => {
    try {
      const data = await apiCall("/api/auth/user/balance")
      return { success: true, balance: data.balance }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const addBalance = async (userId, amount, description = "") => {
    try {
      const data = await apiCall("/api/balance/add", {
        method: "POST",
        body: { userId, amount, description },
      })
      return { success: true, transaction: data.transaction, message: data.message }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const removeBalance = async (userId, amount, description = "") => {
    try {
      const data = await apiCall("/api/balance/remove", {
        method: "POST",
        body: { userId, amount, description },
      })
      return { success: true, transaction: data.transaction, message: data.message }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const transferBalance = async (toUserId, amount, description = "") => {
    try {
      const data = await apiCall("/api/balance/transfer", {
        method: "POST",
        body: { toUserId, amount, description },
      })
      return { success: true, transaction: data.transaction, message: data.message }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const getTransactionHistory = async (page = 1, limit = 20, type = "", userId = "") => {
    try {
      const params = new URLSearchParams({ page, limit })
      if (type) params.append("type", type)
      if (userId) params.append("userId", userId)

      const data = await apiCall(`/api/balance/transactions?${params}`)
      return { success: true, data: data.data, pagination: data.pagination }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const getBalanceSummary = async (startDate = "", endDate = "") => {
    try {
      const params = new URLSearchParams()
      if (startDate) params.append("startDate", startDate)
      if (endDate) params.append("endDate", endDate)

      const data = await apiCall(`/api/balance/summary?${params}`)
      return { success: true, currentBalance: data.currentBalance, summary: data.summary }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  // Email verification functions
  const sendVerificationEmail = async (email) => {
    try {
      const data = await apiCall("/api/auth/send-verification", {
        method: "POST",
        body: { email },
      })
      return { success: true, message: data.message }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const verifyEmail = async (email, code) => {
    try {
      const data = await apiCall("/api/auth/verify-email", {
        method: "POST",
        body: { email, code },
      })
      return { success: true, message: data.message }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  // Pricing management functions
  // fetch users pricing with optional pagination/search
  const getAllUsersPricing = async (page = 1, limit = 20, search = "") => {
    try {
      const params = new URLSearchParams({ page, limit })
      if (search) params.append("search", search)
      const data = await apiCall(`/api/pricing/users?${params.toString()}`)
      return { success: true, users: data.users, defaultPricing: data.defaultPricing, pagination: data.pagination }
    } catch (error) {
      console.error("Error in getAllUsersPricing:", error)
      return { success: false, error: error.message }
    }
  }

  const getUserPricing = async (userId) => {
    try {
      const data = await apiCall(`/api/pricing/user/${userId}`)
      return { success: true, pricing: data }
    } catch (error) {
      console.error("Error in getUserPricing:", error)
      return { success: false, error: error.message }
    }
  }

  const setUserPricing = async (userId, pricingData) => {
    try {
      const results = []

      for (const [messageType, pricePerMessage] of Object.entries(pricingData)) {
        if (pricePerMessage && pricePerMessage !== "") {
          const data = await apiCall(`/api/pricing/user/${userId}`, {
            method: "POST",
            body: {
              messageType,
              pricePerMessage: Number.parseFloat(pricePerMessage),
            },
          })
          results.push(data)
        }
      }

      return { success: true, results, message: "Pricing updated successfully" }
    } catch (error) {
      console.error("Error in setUserPricing:", error)
      return { success: false, error: error.message }
    }
  }

  const setUserBilling = async (userId, billingData) => {
    try {
      const data = await apiCall(`/api/pricing/user/${userId}/billing`, {
        method: "PATCH",
        body: billingData,
      })
      return { success: true, billing: data.billing, reporting: data.reporting }
    } catch (error) {
      console.error("Error in setUserBilling:", error)
      return { success: false, error: error.message }
    }
  }

  const updateUserPricing = async (userId, pricingData) => {
    try {
      const data = await apiCall(`/api/pricing/user/${userId}`, {
        method: "PUT",
        body: pricingData,
      })
      return { success: true, pricing: data.pricing, message: data.message }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const deleteUserPricing = async (userId) => {
    try {
      const data = await apiCall(`/api/pricing/user/${userId}`, {
        method: "DELETE",
      })
      return { success: true, message: data.message }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const getDefaultPricing = async () => {
    try {
      const data = await apiCall("/api/pricing/default")
      return { success: true, pricing: data }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const setDefaultPricing = async (pricingData) => {
    try {
      const data = await apiCall("/api/pricing/default", {
        method: "POST",
        body: pricingData,
      })
      return { success: true, pricing: data.pricing, message: data.message }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }
  
  // makeDefaultPhone: safer/full implementation
const makeDefaultPhone = async (wabaId, phoneId) => {
  try {
    if (!wabaId || !phoneId) {
      return { success: false, error: "Missing wabaId or phoneId" };
    }

    // 👇 send correct property name as backend expects
    const data = await apiCall(`/api/waba/${wabaId}/default-phone`, {
      method: "PUT",
      body: { defaultPhoneNumberId: phoneId },
    });

    return { success: true, ...(data || {}) };
  } catch (err) {
    console.error("setWabaDefaultPhone error:", err);
    return { success: false, error: err.message || "Network error" };
  }
};


  // User profile management functions
  const getUserProfile = async (userId) => {
    try {
      let endpoint

      if (userId) {
        endpoint = `/api/auth/profile/${userId}` // specific user
      } else {
        endpoint = `/api/auth/me` // logged-in user's own profile
      }

      console.log(`[v0] Getting user profile with endpoint: ${endpoint}`)
      const data = await apiCall(endpoint)
      // Normalize the fetched user profile
      const normalizedUser = normalizeUser(data.user)
      return { success: true, user: normalizedUser }
    } catch (error) {
      console.error(`[v0] Error getting user profile:`, error)
      const currentStoredUser = normalizeUser(user || getAuthUser())
      const requestedCurrentUser =
        !userId || currentStoredUser?._id === userId || currentStoredUser?.id === userId

      if (requestedCurrentUser && currentStoredUser) {
        try {
          const data = await apiCall("/api/auth/me")
          const normalizedUser = normalizeUser(data.user)
          setUser(normalizedUser)
          updateStoredAuthUser(normalizedUser)
          return { success: true, user: normalizedUser }
        } catch (meError) {
          console.warn("[v0] /api/auth/me fallback failed, using stored active user:", meError.message)
          return { success: true, user: currentStoredUser, fallback: true }
        }
      }

      return { success: false, error: error.message }
    }
  }

  const updateUserProfile = async (userId, profileData) => {
    try {
      const data = await apiCall(`/api/auth/profile/${userId}`, {
        method: "PUT",
        body: profileData,
      })
      // Normalize the updated user profile
      const normalizedUser = normalizeUser(data.user)
      return { success: true, user: normalizedUser, message: data.message }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const addIPToWhitelist = async (userId, ip) => {
    try {
      const data = await apiCall("/api/auth/ip-whitelist/add", {
        method: "POST",
        body: { userId, ip },
      })
      return { success: true, message: data.message }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const removeIPFromWhitelist = async (userId, ip) => {
    try {
      const data = await apiCall("/api/auth/ip-whitelist/remove", {
        method: "POST",
        body: { userId, ip },
      })
      return { success: true, message: data.message }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const toggleIPWhitelistStatus = async (userId, ip, isActive) => {
    try {
      const data = await apiCall("/api/auth/ip-whitelist/toggle", {
        method: "POST",
        body: { userId, ip, isActive },
      })
      return { success: true, message: data.message }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const toggleUserIPWhitelist = async (userId, enabled) => {
    try {
      const data = await apiCall("/api/auth/ip-whitelist/user-toggle", {
        method: "POST",
        body: { userId, enabled },
      })
      return { success: true, message: data.message }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const updateUserSecuritySettings = async (userId, settings) => {
    try {
      const data = await apiCall(`/api/auth/security/${userId}`, {
        method: "PUT",
        body: settings,
      })
      return { success: true, message: data.message, security: data.security }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const startTwoFactorSetup = async (userId) => {
    try {
      const data = await apiCall(`/api/auth/security/${userId}/2fa/setup`, {
        method: "POST",
      })
      return {
        success: true,
        message: data.message,
        secret: data.secret,
        otpauthUrl: data.otpauthUrl,
        qrCodeDataUrl: data.qrCodeDataUrl,
        expiresInSeconds: data.expiresInSeconds,
      }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const confirmTwoFactorSetup = async (userId, code) => {
    try {
      const data = await apiCall(`/api/auth/security/${userId}/2fa/confirm`, {
        method: "POST",
        body: { code },
      })
      return { success: true, message: data.message, security: data.security }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const addIPToBlocklist = async (userId, ip) => {
    try {
      const data = await apiCall("/api/auth/ip-blocklist/add", {
        method: "POST",
        body: { userId, ip },
      })
      return { success: true, message: data.message }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const removeIPFromBlocklist = async (userId, ip) => {
    try {
      const data = await apiCall("/api/auth/ip-blocklist/remove", {
        method: "POST",
        body: { userId, ip },
      })
      return { success: true, message: data.message }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const toggleIPBlocklistStatus = async (userId, ip, isActive) => {
    try {
      const data = await apiCall("/api/auth/ip-blocklist/toggle", {
        method: "POST",
        body: { userId, ip, isActive },
      })
      return { success: true, message: data.message }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const toggleUserIPBlocklist = async (userId, enabled) => {
    try {
      const data = await apiCall("/api/auth/ip-blocklist/user-toggle", {
        method: "POST",
        body: { userId, enabled },
      })
      return { success: true, message: data.message }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const regenerateApiKey = async () => {
    try {
      const data = await apiCall("/api/auth/regenerate-api-key", {
        method: "POST",
      })
      return { success: true, apiKey: data.apiKey, message: data.message }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const getUserApiKey = async () => {
    try {
      const data = await apiCall("/api/auth/api-key")
      return { success: true, apiKey: data.apiKey }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  // Role-based permission helpers
  const canManageUser = (targetUserRole) => {
    if (!user) return false

    const roleHierarchy = {
      superadmin: ["admin", "reseller", "company", "user", "agent"],
      admin: ["reseller", "company", "user", "agent"],
      reseller: ["company", "user"],
      company: ["user"],
      user: ["agent"],
      agent: [],
    }

    return roleHierarchy[user.role]?.includes(targetUserRole) || false
  }

  const getAllWabaAccounts = async (sync = false) => {
    try {
      console.log("[v0] Getting all WABA accounts... sync=", sync)

      if (IS_LOCAL_API) {
        console.warn("[v0] Using local API URL. Make sure the backend is running.")
      }

      const data = await apiCall(`/api/waba/accounts${sync ? "?sync=true" : ""}`)
      console.log("data=", data)

      // Normalize accounts and user
      const normalizedAccounts = (data?.accounts || data?.wabaAccounts || []).map(normalizeUser)
      const normalizedUser = normalizeUser(data?.user || user || {})

      return {
        success: true,
        accounts: normalizedAccounts,
        summary: data.summary || {
          totalWabaAccounts: normalizedAccounts.length,
          verifiedAccounts: normalizedAccounts.filter((acc) => acc.account_review_status === "APPROVED")?.length || 0,
          totalPhoneNumbers: normalizedAccounts.reduce((sum, acc) => sum + (acc.phoneNumberCount || 0), 0) || 0,
          connectedNumbers: normalizedAccounts.filter((acc) => acc.phoneNumbers?.length > 0)?.length || 0,
        },
        user: normalizedUser,
      }
    } catch (error) {
      console.error("[v0] getAllWabaAccounts error:", error)

      if (error.message.includes("Cannot connect to backend")) {
        console.log("[v0] Backend not available, returning demo data")
        return {
          success: true,
          accounts: [],
          summary: {
            totalWabaAccounts: 0,
            verifiedAccounts: 0,
            totalPhoneNumbers: 0,
            connectedNumbers: 0,
          },
          user: user || {},
          isDemoMode: true,
        }
      }

      return { success: false, error: error.message }
    }
  }

  const getWabaAccountDetails = async (wabaId) => {
    try {
      const data = await apiCall(`/api/waba/accounts/${wabaId}`)
      // Normalize phone numbers and user
      const normalizedPhoneNumbers = (data.phoneNumbers || []).map(normalizeUser)
      const normalizedUser = normalizeUser(data.user || user || {})
      return {
        success: true,
        wabaDetails: data.wabaDetails,
        phoneNumbers: normalizedPhoneNumbers,
        summary: data.summary || {},
        user: normalizedUser,
      }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const getPhoneNumberDetails = async (phoneNumberId) => {
    try {
      console.log("[v0] Getting phone number details for:", phoneNumberId)

      if (!token || !user) {
        throw new Error("User not authenticated. Please login first.")
      }

      const data = await apiCall(`/api/waba/phone-numbers/${phoneNumberId}`)

      // Normalize phone details
      const normalizedPhoneDetails = normalizeUser(data.phoneDetails || data.data || {})

      return {
        success: true,
        phoneDetails: normalizedPhoneDetails,
      }
    } catch (error) {
      console.error("[v0] Get phone number details error:", error)

      // Return demo data if backend is not available
      if (error.message.includes("Failed to fetch") || error.message.includes("fetch")) {
        return {
          success: true,
          phoneDetails: {
            id: phoneNumberId,
            display_phone_number: "+91 ",
            verified_name: "",
            quality_rating: "GREEN",
            messaging_limit_tier: "TIER_1000",
            status: "CONNECTED",
            name_status: "APPROVED",
          },
        }
      }

      return { success: false, error: error.message }
    }
  }

  const updateUserWabaConfig = async (wabaId, phoneNumberId) => {
    try {
      const data = await apiCall("/api/waba/config", {
        method: "PUT",
        body: { wabaId, phoneNumberId },
      })
      // Normalize the updated user
      const normalizedUser = normalizeUser(data.user)
      return { success: true, message: data.message, user: normalizedUser }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const addWabaById = async (wabaId) => {
    try {
      console.log("[v0] Adding WABA with ID:", wabaId)
      console.log("[v0] API Base URL:", API_BASE_URL)
      console.log("[v0] Token:", token ? "Present" : "Missing")

      const data = await apiCall("/api/waba/accounts", {
        method: "POST",
        body: { wabaId },
      })

      console.log("[v0] Add WABA response:", data)
      // Normalize the added WABA account and user
      const normalizedWaba = normalizeUser(data.waba || data.account)
      const normalizedUser = normalizeUser(data.user || user || {})
      return {
        success: true,
        message: data.message || "WABA account added successfully",
        waba: normalizedWaba,
        user: normalizedUser,
      }
    } catch (error) {
      console.error("[v0] Add WABA error:", error)
      return { success: false, error: error.message }
    }
  }

  const canDeleteUser = () => {
    return user?.role === "superadmin"
  }

  const canImpersonate = () => {
    return ["admin", "superadmin"].includes(user?.role)
  }

  const canManagePricing = () => {
    return ["admin", "superadmin"].includes(user?.role)
  }

  const canAddBalance = () => {
    return ["superadmin", "admin"].includes(user?.role)
  }

  const canRemoveBalance = () => {
    return ["superadmin", "admin"].includes(user?.role)
  }

  const canTransferBalance = () => {
    return user?.role !== undefined && user?.role !== "agent"
  }

  const canViewAllTransactions = () => {
    return ["superadmin", "admin"].includes(user?.role)
  }

  // Profile permission helpers
  const canEditProfile = (targetUserId) => {
    if (!user) return false

    // Users can edit their own profile
    if (user._id === targetUserId) return true

    // Superadmin, admin, reseller, company can edit profiles they manage
    return ["superadmin", "admin", "reseller", "company", "user"].includes(user.role)
  }

  const canEditWhatsAppCredentials = (targetUserId) => {
    if (!user) return false

    // Users cannot edit WhatsApp credentials
    if (["user", "agent"].includes(user.role)) return false

    // Only superadmin, admin, reseller, company can edit WhatsApp credentials
    return ["superadmin", "admin", "reseller", "company"].includes(user.role)
  }

  const canViewProfile = (targetUserId) => {
    if (!user) return false

    // Users can view their own profile
    if (user._id === targetUserId) return true

    // All other roles can view profiles they manage
    return ["superadmin", "admin", "reseller", "company", "user"].includes(user.role)
  }

  const updateUserCredentials = async (credentials) => {
    try {
      console.log("[v0] Updating user credentials:", credentials)

      const data = await apiCall("/api/waba/credentials", {
        method: "PUT",
        body: credentials,
      })

      console.log("[v0] Backend response:", data)

      if (data.success) {
        // Use the complete updated user data from backend response, not just the credentials
        const updatedUser = normalizeUser(data.user || { ...user, ...credentials })

        console.log("[v0] Setting updated user:", updatedUser)

        setUser(updatedUser)
        updateStoredAuthUser(updatedUser)

        console.log("[v0] User credentials updated and persisted")
      }
      return data
    } catch (error) {
      console.error("[v0] Error updating credentials:", error)
      return { success: false, error: error.message }
    }
  }

  const fetchTemplates = async (userId = "") => {
    try {
      const endpoint = userId ? `/api/templates/user?userId=${userId}` : "/api/templates/user"
      const data = await apiCall(endpoint)
      return data.templates || []
    } catch (error) {
      console.error("Error fetching templates:", error)
      return []
    }
  }

  const syncTemplates = async (userId = "") => {
    try {
      const endpoint = userId ? `/api/sync?userId=${userId}` : "/api/sync"
      const data = await apiCall(endpoint, {
        method: "POST",
      })
      return { success: true, message: data.message, stats: data.stats }
    } catch (error) {
      console.error("Error syncing templates:", error)
      return { success: false, error: error.message }
    }
  }

  const deleteTemplate = async (templateId) => {
    try {
      const data = await apiCall(`/api/${templateId}`, { method: "DELETE" })
      return { success: true, message: data.message }
    } catch (error) {
      console.error("Error deleting template:", error)
      return { success: false, error: error.message }
    }
  }
  
  const syncWabaAccounts = async () => {
  try {
    // Simply call the same endpoint which triggers server sync as part of the handler
    const data = await getAllWabaAccounts()
    return data
  } catch (err) {
    return { success: false, error: err.message }
  }
}

  const fetchUserBalance = async () => {
    try {
      const data = await apiCall("/api/auth/user/balance")
      return data.balance || 0
    } catch (error) {
      console.error("Error fetching user balance:", error)
      return 0
    }
  }

  // Contact API functions
  const fetchContacts = async () => {
    try {
      const contacts = await apiCall("/api/contact")
      // Normalize contacts
      const normalizedContacts = contacts.map(normalizeUser)
      return normalizedContacts
    } catch (error) {
      console.error("Error fetching contacts:", error)
      // Return mock data for development
      return []
    }
  }

  const createContact = async (contactData) => {
    try {
      const contact = await apiCall("/api/contact", {
        method: "POST",
        body: JSON.stringify(contactData),
      })
      // Normalize the created contact
      const normalizedContact = normalizeUser(contact)
      return { success: true, data: normalizedContact }
    } catch (error) {
      console.error("Error creating contact:", error)
      return { success: false, message: error.message }
    }
  }

  const updateContact = async (contactId, contactData) => {
    try {
      const contact = await apiCall(`/api/contact/${contactId}`, {
        method: "PUT",
        body: JSON.stringify(contactData),
      })
      // Normalize the updated contact
      const normalizedContact = normalizeUser(contact)
      return { success: true, data: normalizedContact }
    } catch (error) {
      console.error("Error updating contact:", error)
      return { success: false, message: error.message }
    }
  }

  const deleteContact = async (contactId) => {
    try {
      await apiCall(`/api/contact/${contactId}`, {
        method: "DELETE",
      })
      return { success: true }
    } catch (error) {
      console.error("Error deleting contact:", error)
      return { success: false, error: error.message }
    }
  }

  // Template creation API functions
 const uploadTemplateMedia = async (file) => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const data = await apiCall("/api/templatecreate/upload-media", {
      method: "POST",
      body: formData, // ✅ apiCall handles auth
    });

    return { success: true, header_handle: data.header_handle };
  } catch (error) {
    console.error("Error uploading template media:", error.message);
    return { success: false, error: error.message };
  }
};


  const createTemplate = async (templateData) => {
    try {
      const data = await apiCall("/api/templatecreate/create", {
        method: "POST",
        body: templateData,
      })

      return { success: true, template: data.template, message: "Template created successfully" }
    } catch (error) {
      console.error("Error creating template:", error)
      return { success: false, error: error.message, details: error.details }
    }
  }

  const getTemplates = async () => {
    try {
      const data = await apiCall("/api/templatecreate/templates")
      return { success: true, templates: data.templates || [] }
    } catch (error) {
      console.error("Error fetching templates:", error)
      return { success: false, error: error.message, templates: [] }
    }
  }

  const bulkUploadContacts = async (file, groupId) => {
    try {
      const formData = new FormData()
      formData.append("file", file)
      formData.append("group", groupId)

      const result = await apiCall("/api/contact/bulk-upload", {
        method: "POST",
        headers: {}, // Remove Content-Type to let browser set it for FormData
        body: formData,
      })
      // Normalize uploaded contacts
      const normalizedResult = {
        ...result,
        data: (result.data || []).map(normalizeUser),
      }
      return { success: true, data: normalizedResult }
    } catch (error) {
      console.error("Error bulk uploading contacts:", error)
      return { success: false, message: error.message }
    }
  }

  const getContactsByGroup = async (groupId) => {
    try {
      const contacts = await apiCall(`/api/contact/by-group/${groupId}`)
      // Normalize contacts
      const normalizedContacts = contacts.map(normalizeUser)
      return normalizedContacts
    } catch (error) {
      console.error("Error fetching contacts by group:", error)
      return []
    }
  }

  // Group API functions
  const fetchGroups = async () => {
    try {
      const groups = await apiCall("/api/groups")
      console.log("Fetched groups:", groups)
      return groups || []
    } catch (error) {
      console.error("Error fetching groups:", error)
      return []
    }
  }

  const createGroup = async (groupData) => {
    try {
      const group = await apiCall("/api/groups/create", {
        method: "POST",
        body: JSON.stringify(groupData),
      })
      return { success: true, data: group }
    } catch (error) {
      console.error("Error creating group:", error)
      return { success: false, message: error.message }
    }
  }

  const updateGroup = async (groupId, groupData) => {
    try {
      const group = await apiCall(`/api/groups/${groupId}`, {
        method: "PUT",
        body: JSON.stringify(groupData),
      })
      return { success: true, data: group }
    } catch (error) {
      console.error("Error updating group:", error)
      return { success: false, message: error.message }
    }
  }

  const deleteGroup = async (groupId) => {
    try {
      await apiCall(`/api/groups/${groupId}`, {
        method: "DELETE",
      })
      return { success: true, message: "Group and its contacts deleted successfully" }
    } catch (error) {
      console.error("Error deleting group:", error)
      return { success: false, message: error.message }
    }
  }

  // Campaign history API functions
  const fetchAllUsers = async () => {
    try {
      const token = getAuthToken()
      const response = await fetch(`${API_BASE_URL}/api/auth/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })

      if (response.ok) {
        const data = await response.json()
        // Normalize users
        const normalizedUsers = (data.users || data || []).map(normalizeUser)
        return normalizedUsers
      }
      return []
    } catch (error) {
      console.error("Error fetching users:", error)
      return []
    }
  }

  const fetchCampaignHistory = async (userId = null, filters = {}, page = 1, limit = 50) => {
    try {
      const token = getAuthToken()

      // Build query parameters
      const params = new URLSearchParams()
      params.append("page", page.toString())
      params.append("limit", limit.toString())

      if (filters.campaignId) params.append("campaignId", filters.campaignId)
      if (filters.messageType) params.append("messageType", filters.messageType)
      if (filters.status) params.append("status", filters.status)
      if (filters.startDate) params.append("startDate", filters.startDate)
      if (filters.endDate) params.append("endDate", filters.endDate)

      // Determine endpoint based on userId
      const endpoint = userId
        ? `${API_BASE_URL}/api/history/user/${userId}?${params.toString()}`
        : `${API_BASE_URL}/api/history/all?${params.toString()}`

      const response = await fetch(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })

      if (response.ok) {
        const data = await response.json()
        return {
          success: true,
          history: data.history || [],
          pagination: data.pagination || {
            currentPage: page,
            totalPages: 1,
            totalRecords: 0,
            hasNext: false,
            hasPrev: false,
          },
          summary: data.summary || data.overallStats || {},
          messageTypeStats: data.messageTypeStats || {},
        }
      }

      throw new Error("Failed to fetch campaign history")
    } catch (error) {
      console.error("Error fetching campaign history:", error)
      return {
        success: false,
        history: [],
        pagination: {
          currentPage: page,
          totalPages: 1,
          totalRecords: 0,
          hasNext: false,
          hasPrev: false,
        },
        summary: {},
        messageTypeStats: {},
        error: error.message,
      }
    }
  }

  const exportCampaignHistory = async (userId = null, filters = {}, format = "excel") => {
    try {
      const token = getAuthToken()

      const params = new URLSearchParams()
      if (filters.campaignId) params.append("campaignId", filters.campaignId)
      if (filters.messageType) params.append("messageType", filters.messageType)
      if (filters.status) params.append("status", filters.status)
      if (filters.startDate) params.append("startDate", filters.startDate)
      if (filters.endDate) params.append("endDate", filters.endDate)
      params.append("format", format)

      const endpoint = userId
        ? `${API_BASE_URL}/api/history/user/${userId}/export?${params.toString()}`
        : `${API_BASE_URL}/api/history/all/export?${params.toString()}`

      const response = await fetch(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = `campaign_history_${userId || "all"}_${new Date().toISOString().split("T")[0]}.${format === "excel" ? "xlsx" : "csv"}`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
        return { success: true }
      }

      throw new Error("Export failed")
    } catch (error) {
      console.error("Error exporting campaign history:", error)
      throw error
    }
  }

  // Campaign management functions
  const fetchCampaignTemplates = async () => {
    try {
      const token = getAuthToken()
      const response = await fetch(`${API_BASE_URL}/api/campaigns/templates`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })

      if (response.ok) {
        const data = await response.json()
        return data.templates || []
      }
      return []
    } catch (error) {
      console.error("Error fetching campaign templates:", error)
      return []
    }
  }

  const createCampaign = async (campaignData) => {
    try {
      const token = getAuthToken()
      const response = await fetch(`${API_BASE_URL}/api/campaigns/create`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(campaignData),
      })

      if (response.ok) {
        return await response.json()
      }
      throw new Error("Failed to create campaign")
    } catch (error) {
      console.error("Error creating campaign:", error)
      throw error
    }
  }

  const uploadCampaignVariables = async (campaignId, file) => {
    try {
      const token = getAuthToken()
      const formData = new FormData()
      formData.append("excel", file)

      const response = await fetch(`${API_BASE_URL}/api/campaigns/${campaignId}/upload-variables`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      })

      if (response.ok) {
        return await response.json()
      }
      throw new Error("Failed to upload variables")
    } catch (error) {
      console.error("Error uploading campaign variables:", error)
      throw error
    }
  }

  const saveCampaignMapping = async (campaignId, mappingData) => {
    try {
      const token = getAuthToken()
      const response = await fetch(`${API_BASE_URL}/api/campaigns/${campaignId}/save-mapping`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(mappingData),
      })

      if (response.ok) {
        return await response.json()
      }
      throw new Error("Failed to save mapping")
    } catch (error) {
      console.error("Error saving campaign mapping:", error)
      throw error
    }
  }

  const uploadCampaignPhoneNumbers = async (campaignId, file) => {
    try {
      const token = getAuthToken()
      const formData = new FormData()
      formData.append("excel", file)

      const response = await fetch(`${API_BASE_URL}/api/campaigns/${campaignId}/upload-numbers`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      })

      if (response.ok) {
        return await response.json()
      }
      throw new Error("Failed to upload phone numbers")
    } catch (error) {
      console.error("Error uploading campaign numbers:", error)
      throw error
    }
  }

  const sendCampaign = async (campaignId) => {
    try {
      const token = getAuthToken()
      const response = await fetch(`${API_BASE_URL}/api/campaigns/${campaignId}/send`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })

      if (response.ok) {
        return await response.json()
      }
      throw new Error("Failed to send campaign")
    } catch (error) {
      console.error("Error sending campaign:", error)
      throw error
    }
  }

  const getCampaign = async (campaignId) => {
    try {
      const token = getAuthToken()
      const response = await fetch(`${API_BASE_URL}/api/campaigns/${campaignId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })

      if (response.ok) {
        return await response.json()
      }
      throw new Error("Failed to get campaign")
    } catch (error) {
      console.error("Error getting campaign:", error)
      throw error
    }
  }

  const getCampaignStatus = async (campaignId) => {
    try {
      const token = getAuthToken()
      const response = await fetch(`${API_BASE_URL}/api/campaigns/${campaignId}/status`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })

      if (response.ok) {
        return await response.json()
      }
      throw new Error("Failed to get campaign status")
    } catch (error) {
      console.error("Error getting campaign status:", error)
      throw error
    }
  }

  const getUserCampaigns = async ({ userId = "", status = "", page = 1, limit = 50 } = {}) => {
    try {
      const token = getAuthToken()
      const params = new URLSearchParams()
      params.append("page", page.toString())
      params.append("limit", limit.toString())
      if (status) params.append("status", status)
      if (userId) params.append("userId", userId)

      const response = await fetch(`${API_BASE_URL}/api/campaigns/user/campaigns?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })

      if (response.ok) {
        const data = await response.json()
        return {
          success: true,
          campaigns: data.campaigns || [],
          summary: data.summary || {},
          pagination: data.pagination || { page, limit, total: 0, pages: 0 },
        }
      }
      return { success: false, campaigns: [], summary: {}, pagination: { page, limit, total: 0, pages: 0 } }
    } catch (error) {
      console.error("Error fetching user campaigns:", error)
      return { success: false, campaigns: [], summary: {}, pagination: { page, limit, total: 0, pages: 0 }, error: error.message }
    }
  }

  const checkCampaignBalance = async (campaignId) => {
    try {
      const token = getAuthToken()
      const response = await fetch(`${API_BASE_URL}/api/campaigns/${campaignId}/check-balance`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })

      if (response.ok) {
        return await response.json()
      }
      throw new Error("Failed to check campaign balance")
    } catch (error) {
      console.error("Error checking campaign balance:", error)
      throw error
    }
  }

  const getQueueStats = async () => {
    try {
      const token = getAuthToken()
      const response = await fetch(`${API_BASE_URL}/api/campaigns/queue/stats`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })

      if (response.ok) {
        return await response.json()
      }
      return {}
    } catch (error) {
      console.error("Error fetching queue stats:", error)
      return {}
    }
  }

  const getAllMessages = async ({ page = 1, limit = 100, status = "" } = {}) => {
    try {
      const token = getAuthToken()
      const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() })
      if (status) params.append("status", status)
      params.append("all", "true")

      const response = await fetch(`${API_BASE_URL}/api/messages?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })

      if (response.ok) {
        const data = await response.json()
        return {
          success: true,
          data: data.data || [],
          pagination: data.pagination || { page, limit, total: 0, pages: 0 },
        }
      }

      return { success: false, data: [], pagination: { page, limit, total: 0, pages: 0 } }
    } catch (error) {
      console.error("Error fetching all messages:", error)
      return { success: false, data: [], pagination: { page, limit, total: 0, pages: 0 }, error: error.message }
    }
  }

  const getAllRecipients = async ({ page = 1, limit = 100, status = "all", userId = "all", operator = "all", messageType = "all", startDate = "", endDate = "", search = "" } = {}) => {
    try {
      const token = getAuthToken()
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      })
      if (status) params.append("status", status)
      if (userId) params.append("userId", userId)
      if (operator) params.append("operator", operator)
      if (messageType) params.append("messageType", messageType)
      if (startDate) params.append("startDate", startDate)
      if (endDate) params.append("endDate", endDate)
      if (search) params.append("search", search)

      const response = await fetch(`${API_BASE_URL}/api/campaigns/queue/all-recipients?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })

      if (response.ok) {
        const data = await response.json()
        return {
          success: true,
          data: data.data || [],
          pagination: data.pagination || { page, limit, total: 0, pages: 0 },
        }
      }

      return { success: false, data: [], pagination: { page, limit, total: 0, pages: 0 } }
    } catch (error) {
      console.error("Error fetching all recipients:", error)
      return { success: false, data: [], pagination: { page, limit, total: 0, pages: 0 }, error: error.message }
    }
  }

  const processQueuedCampaigns = async () => {
    try {
      const token = getAuthToken()
      const response = await fetch(`${API_BASE_URL}/api/campaigns/process-queued`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })

      if (response.ok) {
        return await response.json()
      }
      throw new Error("Failed to process queued campaigns")
    } catch (error) {
      console.error("Error processing queued campaigns:", error)
      throw error
    }
  }

  const getCampaignMessages = async (campaignId, page = 1, limit = 50) => {
    try {
      const token = getAuthToken()
      const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() })
      const response = await fetch(`${API_BASE_URL}/api/campaigns/${campaignId}/messages?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })

      if (response.ok) {
        return await response.json()
      }
      throw new Error("Failed to get campaign messages")
    } catch (error) {
      console.error("Error fetching campaign messages:", error)
      return { success: false, data: [], pagination: { page, limit, total: 0, pages: 0 }, error: error.message }
    }
  }

  // Media management functions
  const uploadMedia = async (file) => {
    try {
      const token = getAuthToken()
      const formData = new FormData()
      formData.append("media", file)

      const response = await fetch(`${API_BASE_URL}/api/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      })

      if (response.ok) {
        return await response.json()
      }
      throw new Error("Failed to upload media")
    } catch (error) {
      console.error("Error uploading media:", error)
      throw error
    }
  }

  const getUserMedia = async (page = 1, limit = 10, fileType = null) => {
    try {
      const token = getAuthToken()
      const params = new URLSearchParams()
      params.append("page", page.toString())
      params.append("limit", limit.toString())
      if (fileType) params.append("fileType", fileType)

      const response = await fetch(`${API_BASE_URL}/api/user-media?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })

      if (response.ok) {
        return await response.json()
      }
      return { data: [], pagination: { total: 0, pages: 1, currentPage: 1, limit: limit } }
    } catch (error) {
      console.error("Error fetching user media:", error)
      return { data: [], pagination: { total: 0, pages: 1, currentPage: 1, limit: limit } }
    }
  }

  const deleteMedia = async (mediaId) => {
    try {
      const token = getAuthToken()
      const response = await fetch(`${API_BASE_URL}/api/delete/${mediaId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })

      if (response.ok) {
        return await response.json()
      }
      throw new Error("Failed to delete media")
    } catch (error) {
      console.error("Error deleting media:", error)
      throw error
    }
  }

  const fetchCampaignsReports = async (filters = {}, page = 1, limit = 10) => {
    try {
      const token = getAuthToken()

      // Build query parameters
      const params = new URLSearchParams()
      params.append("page", page.toString())
      params.append("limit", limit.toString())
      if (filters.startDate) params.append("startDate", filters.startDate)
      if (filters.endDate) params.append("endDate", filters.endDate)
      if (filters.status && filters.status !== "all") params.append("status", filters.status)
      if (filters.search) params.append("search", filters.search)

      const response = await fetch(`${API_BASE_URL}/api/campaigns/reports?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })

      if (response.ok) {
        const data = await response.json()
        return {
          data: data.data || data.campaigns || [],
          pagination: data.pagination || {
            total: data.total || 0,
            pages: data.pages || Math.ceil((data.total || 0) / limit),
            currentPage: page,
            limit: limit,
          },
        }
      }

      return {
        data: [],
        pagination: { total: 0, pages: 1, currentPage: 1, limit: limit },
      }
    } catch (error) {
      console.error("Error fetching campaigns:", error)
      return {
        data: [],
        pagination: { total: 0, pages: 1, currentPage: 1, limit: limit },
      }
    }
  }

  const fetchCampaignMessagesReports = async (campaignId, page = 1, limit = 10) => {
    try {
      const token = getAuthToken()

      const params = new URLSearchParams()
      params.append("page", page.toString())
      params.append("limit", limit.toString())

      const response = await fetch(`${API_BASE_URL}/api/campaigns/${campaignId}/messages?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })

      if (response.ok) {
        const data = await response.json()
        return {
          data: data.messages || data.data || [],
          pagination: {
            total: data.pagination?.total || 0,
            pages: data.pagination?.pages || Math.ceil((data.pagination?.total || 0) / limit),
            currentPage: page,
            limit: limit,
          },
        }
      }

      return {
        data: [],
        pagination: { total: 0, pages: 1, currentPage: 1, limit: limit },
      }
    } catch (error) {
      console.error("Error fetching campaign messages:", error)
      return {
        data: [],
        pagination: { total: 0, pages: 1, currentPage: 1, limit: limit },
      }
    }
  }

  const exportCampaignReportReports = async (campaignId, format = "excel") => {
    try {
      const token = getAuthToken()

      const response = await fetch(`${API_BASE_URL}/api/campaigns/${campaignId}/export?format=${format}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.ok) {
        if (format === "excel") {
          // Handle Excel download
          const blob = await response.blob()
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement("a")
          a.href = url
          a.download = `campaign_report_${campaignId}.xlsx`
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          window.URL.revokeObjectURL(url)
          return { success: true }
        } else {
          // Return data for PDF/CSV processing
          return await response.json()
        }
      }

      throw new Error("Export failed")
    } catch (error) {
      console.error("Error exporting campaign report:", error)
      throw error
    }
  }

  const getUserTemplates = async () => {
    try {
      const data = await apiCall("/api/user")
      return { success: true, templates: data.templates, count: data.count }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const syncTemplates2 = async () => {
    try {
      const data = await apiCall("/api/sync", {
        method: "POST",
      })
      return { success: true, message: data.message, stats: data.stats }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const getTemplateById = async (templateId) => {
    try {
      const data = await apiCall(`/api/templates/${templateId}`)
      return { success: true, template: data.template }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const getUserMedia2 = async (page = 1, limit = 10, fileType = "") => {
    try {
      const params = new URLSearchParams({ page, limit })
      if (fileType) params.append("fileType", fileType)

      const data = await apiCall(`/user-media?${params}`)
      return { success: true, data: data.data, pagination: data.pagination }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const uploadMedia2 = async (formData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/upload`, {
        method: "POST",
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: formData,
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || "Upload failed")
      }

      const data = await response.json()
      return { success: true, media: data.data }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const createCampaign2 = async (campaignData) => {
    try {
      const data = await apiCall("/api/campaigns/create", {
        method: "POST",
        body: campaignData,
      })
      return { success: true, campaign: data.campaign, message: data.message }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const sendCampaignMessage = async (messageData) => {
    try {
      const data = await apiCall("/api/campaigns/send-message", {
        method: "POST",
        body: messageData,
      })
      return { success: true, message: data.message, messageId: data.messageId }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const sendCampaign2 = async (campaignId) => {
    try {
      const data = await apiCall(`/api/campaigns/${campaignId}/send`, {
        method: "POST",
      })
      return { success: true, message: data.message, campaign: data.campaign }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }
  
  const retryPendingCampaign = async (campaignId) => {
  try {
    const token = getAuthToken()
    const response = await fetch(`${API_BASE_URL}/api/campaigns/${campaignId}/retry-pending`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })

    if (!response.ok) {
      const errText = await response.text()
      throw new Error(errText || "Retry campaign failed")
    }

    const data = await response.json()
    return { success: true, ...data, message: data.message || "Retry started successfully" }
  } catch (error) {
    console.error("Error retrying campaign:", error)
    return { success: false, error: error.message }
  }
}

  const getCampaignStatus2 = async (campaignId) => {
    try {
      const data = await apiCall(`/api/campaigns/${campaignId}/status`)
      return { success: true, campaign: data.campaign }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const getUserCampaigns2 = async (page = 1, limit = 10) => {
    try {
      const params = new URLSearchParams({ page, limit })
      const data = await apiCall(`/api/campaigns/user/campaigns?${params}`)
      return { success: true, campaigns: data.campaigns, pagination: data.pagination }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  // Support ticket methods
  const createSupportTicket = async (ticketData) => {
    try {
      const isFormData = ticketData instanceof FormData
      const response = await fetch(`${API_BASE_URL}/api/support/tickets`, {
        method: "POST",
        headers: {
          ...(isFormData ? {} : { "Content-Type": "application/json" }),
          Authorization: `Bearer ${token}`,
        },
        body: isFormData ? ticketData : JSON.stringify(ticketData),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || "Failed to create support ticket")
      }

      return data
    } catch (error) {
      console.error("Error creating support ticket:", error)
      throw error
    }
  }

  const getSupportTickets = async (params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString()
      const url = `${API_BASE_URL}/api/support/tickets${queryParams ? `?${queryParams}` : ""}`

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch support tickets")
      }

      return data
    } catch (error) {
      console.error("Error fetching support tickets:", error)
      return { success: false, data: [], pagination: { currentPage: 1, totalPages: 1, totalTickets: 0 } }
    }
  }

  const getSupportTicket = async (ticketId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/support/tickets/${ticketId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch support ticket")
      }

      return data
    } catch (error) {
      console.error("Error fetching support ticket:", error)
      throw error
    }
  }

  const updateSupportTicket = async (ticketId, updateData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/support/tickets/${ticketId}/status`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updateData),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || "Failed to update support ticket status")
      }

      return data
    } catch (error) {
      console.error("Error updating support ticket status:", error)
      throw error
    }
  }

  const updateSupportTicketPriority = async (ticketId, priority) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/support/tickets/${ticketId}/priority`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ priority }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || "Failed to update support ticket priority")
      }

      return data
    } catch (error) {
      console.error("Error updating support ticket priority:", error)
      throw error
    }
  }

  const reopenSupportTicket = async (ticketId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/support/tickets/${ticketId}/reopen`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || "Failed to reopen support ticket")
      }

      return data
    } catch (error) {
      console.error("Error reopening support ticket:", error)
      throw error
    }
  }

  const assignSupportTicket = async (ticketId, assignToEmail) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/support/tickets/${ticketId}/assign`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ assignToEmail }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || "Failed to assign support ticket")
      }

      return data
    } catch (error) {
      console.error("Error assigning support ticket:", error)
      throw error
    }
  }

  const addSupportTicketResponse = async (ticketId, responseData) => {
    try {
      const isFormData = responseData instanceof FormData
      const response = await fetch(`${API_BASE_URL}/api/support/tickets/${ticketId}/responses`, {
        method: "POST",
        headers: {
          ...(isFormData ? {} : { "Content-Type": "application/json" }),
          Authorization: `Bearer ${token}`,
        },
        body: isFormData ? responseData : JSON.stringify(responseData),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || "Failed to add response")
      }

      return data
    } catch (error) {
      console.error("Error adding support ticket response:", error)
      throw error
    }
  }

  const getSupportTicketStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/support/tickets/stats`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch support ticket stats")
      }

      return data.data
    } catch (error) {
      console.error("Error fetching support ticket stats:", error)
      throw error
    }
  }

  const exportSupportTickets = async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString()
      const url = `${API_BASE_URL}/api/support/tickets/export${queryParams ? `?${queryParams}` : ""}`
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      if (response.ok) {
        const blob = await response.blob()
        const downloadUrl = window.URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = downloadUrl
        a.download = `support_tickets_export_${Date.now()}.xlsx`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(downloadUrl)
        return { success: true }
      }
      throw new Error("Export failed")
    } catch (error) {
      console.error("Error exporting support tickets:", error)
      throw error
    }
  }

  const getSupportNotifications = async (unreadOnly = false) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/support/notifications?unreadOnly=${unreadOnly}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch notifications")
      }

      return data
    } catch (error) {
      console.error("Error fetching notifications:", error)
      throw error
    }
  }

  const markSupportNotificationsRead = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/support/notifications/read`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || "Failed to mark notifications read")
      }

      return data
    } catch (error) {
      console.error("Error marking notifications read:", error)
      throw error
    }
  }

  const fetchReportStats = async (filters = {}) => {
    const defaultLast24Stats = {
      total: 0,
      sent: 0,
      delivered: 0,
      read: 0,
      failed: 0,
      pending: 0,
      creditsUsed: 0,
      totalCost: 0,
      totalSent: 0,
    }
    const defaultStats = {
      total: 0,
      sent: 0,
      delivered: 0,
      read: 0,
      failed: 0,
      pending: 0,
      creditsUsed: 0,
      totalCost: 0,
      campaigns: 0,
      totalSent: 0,
      last24: defaultLast24Stats,
    }

    try {
      const token = getAuthToken()

      // Build query parameters
      const params = new URLSearchParams()
      if (filters.startDate) params.append("startDate", filters.startDate)
      if (filters.endDate) params.append("endDate", filters.endDate)
      if (filters.status && filters.status !== "all") params.append("status", filters.status)
      if (filters.search) params.append("search", filters.search)
      if (filters.numberSearch) params.append("numberSearch", filters.numberSearch)
      if (filters.operator && filters.operator !== "all") params.append("operator", filters.operator)
      if (filters.sendVia && filters.sendVia !== "all") params.append("sendVia", filters.sendVia)
      if (filters.senderNumber && filters.senderNumber !== "all") params.append("senderNumber", filters.senderNumber)
      if (filters.userId) params.append("userId", filters.userId)

      const response = await fetch(`${API_BASE_URL}/api/reports/stats?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })

      if (response.ok) {
        const data = await response.json()
        const stats = data.stats || data || {}
        return {
          ...defaultStats,
          ...stats,
          last24: data.last24 || stats.last24 || defaultLast24Stats,
        }
      }

      return defaultStats
    } catch (error) {
      console.error("Error fetching report stats:", error)
      return defaultStats
    }
  }

  const fetchCampaigns = async (filters = {}, page = 1, limit = 10) => {
    try {
      const token = getAuthToken()

      // Build query parameters
      const params = new URLSearchParams()
      params.append("page", page.toString())
      params.append("limit", limit.toString())
      if (filters.startDate) params.append("startDate", filters.startDate)
      if (filters.endDate) params.append("endDate", filters.endDate)
      if (filters.status && filters.status !== "all") params.append("status", filters.status)
      if (filters.search) params.append("search", filters.search)
      if (filters.numberSearch) params.append("numberSearch", filters.numberSearch)
      if (filters.operator && filters.operator !== "all") params.append("operator", filters.operator)
      if (filters.sendVia && filters.sendVia !== "all") params.append("sendVia", filters.sendVia)
      if (filters.senderNumber && filters.senderNumber !== "all") params.append("senderNumber", filters.senderNumber)
      if (filters.userId) params.append("userId", filters.userId)

      const response = await fetch(`${API_BASE_URL}/api/reports/campaigns?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })

      if (response.ok) {
        const data = await response.json()
        return {
          data: data.data || data.campaigns || [],
          pagination: data.pagination || {
            total: data.total || 0,
            pages: data.pages || Math.ceil((data.total || 0) / limit),
            currentPage: page,
            limit: limit,
          },
        }
      }

      return {
        data: [],
        pagination: { total: 0, pages: 1, currentPage: 1, limit: limit },
      }
    } catch (error) {
      console.error("Error fetching campaigns:", error)
      return {
        data: [],
        pagination: { total: 0, pages: 1, currentPage: 1, limit: limit },
      }
    }
  }

  const fetchCampaignMessages = async (campaignId, page = 1, limit = 10, filters = {}) => {
    try {
      const token = getAuthToken()

      const params = new URLSearchParams()
      params.append("page", page.toString())
      params.append("limit", limit.toString())
      if (filters.status && filters.status !== "all") params.append("status", filters.status)
      if (filters.numberSearch) params.append("numberSearch", filters.numberSearch)

      const response = await fetch(
        `${API_BASE_URL}/api/reports/campaigns/${campaignId}/messages?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      )

      if (response.ok) {
        const data = await response.json()
        return {
          data: data.data || data.messages || [],
          pagination: {
            total: data.pagination?.total || 0,
            pages: data.pagination?.pages || Math.ceil((data.pagination?.total || 0) / limit),
            currentPage: page,
            limit: limit,
          },
        }
      }

      return {
        data: [],
        pagination: { total: 0, pages: 1, currentPage: 1, limit: limit },
      }
    } catch (error) {
      console.error("Error fetching campaign messages:", error)
      return {
        data: [],
        pagination: { total: 0, pages: 1, currentPage: 1, limit: limit },
      }
    }
  }

  const fetchCampaignConversationalReport = async (campaignId, filters = {}, page = 1, limit = 10) => {
    try {
      const token = getAuthToken()
      const params = new URLSearchParams()
      params.append("page", page.toString())
      params.append("limit", limit.toString())
      if (filters.status && filters.status !== "all") params.append("status", filters.status)
      if (filters.numberSearch) params.append("numberSearch", filters.numberSearch)
      if (filters.search) params.append("search", filters.search)

      const response = await fetch(
        `${API_BASE_URL}/api/reports/campaigns/${campaignId}/conversations?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      )

      if (response.ok) {
        const data = await response.json()
        return {
          success: true,
          campaign: data.campaign || {},
          stats: data.stats || {},
          recipients: data.recipients || [],
          pagination: data.pagination || { total: 0, pages: 1, page: page, limit: limit }
        }
      }

      return { success: false, error: "Failed to fetch conversational report" }
    } catch (error) {
      console.error("Error fetching campaign conversational report:", error)
      return { success: false, error: error.message }
    }
  }

  const fetchRecipientConversationHistory = async (campaignId, number) => {
    try {
      const token = getAuthToken()
      const response = await fetch(
        `${API_BASE_URL}/api/reports/campaigns/${campaignId}/recipients/${encodeURIComponent(number)}/messages`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      )

      if (response.ok) {
        const data = await response.json()
        return {
          success: true,
          recipient: data.recipient || {},
          messages: data.messages || []
        }
      }

      return { success: false, error: "Failed to fetch recipient conversation history" }
    } catch (error) {
      console.error("Error fetching recipient conversation history:", error)
      return { success: false, error: error.message }
    }
  }

  const exportCampaignConversationalReport = async (campaignId, filters = {}) => {
    try {
      const token = getAuthToken()
      const params = new URLSearchParams()
      if (filters.status && filters.status !== "all") params.set("status", filters.status)
      if (filters.numberSearch) params.set("numberSearch", filters.numberSearch)

      const response = await fetch(`${API_BASE_URL}/api/reports/campaigns/${campaignId}/conversational-export?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      if (response.ok) {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = `Conversational_Report_${campaignId}.xlsx`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
        return { success: true }
      }
      return { success: false, error: "Failed to export conversational report" }
    } catch (error) {
      console.error("Error exporting conversational report:", error)
      return { success: false, error: error.message }
    }
  }

  const exportCampaignReport = async (campaignId, format = "excel", filters = {}) => {
    try {
      const token = getAuthToken()

      const params = new URLSearchParams()
      params.set("format", format)
      if (filters.startDate) params.set("startDate", filters.startDate)
      if (filters.endDate) params.set("endDate", filters.endDate)
      if (filters.status && filters.status !== "all") params.set("status", filters.status)
      if (filters.operator && filters.operator !== "all") params.set("operator", filters.operator)
      if (filters.sendVia && filters.sendVia !== "all") params.set("sendVia", filters.sendVia)
      if (filters.senderNumber && filters.senderNumber !== "all") params.set("senderNumber", filters.senderNumber)

      const response = await fetch(`${API_BASE_URL}/api/reports/campaigns/${campaignId}/export?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.ok) {
        if (format === "excel") {
          // Handle Excel download
          const blob = await response.blob()
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement("a")
          a.href = url
          a.download = `campaign_report_${campaignId}.xlsx`
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          window.URL.revokeObjectURL(url)
          return { success: true }
        } else {
          return await response.json()
        }
      }
    } catch (error) {
      console.error("Error exporting campaign report:", error)
      throw error
    }
  }

  const exportFilteredCampaigns = async (filters = {}, format = "excel") => {
    try {
      const token = getAuthToken()
      const params = new URLSearchParams()
      params.set("format", format)
      if (filters.startDate) params.set("startDate", filters.startDate)
      if (filters.endDate) params.set("endDate", filters.endDate)
      if (filters.status && filters.status !== "all") params.set("status", filters.status)
      if (filters.operator && filters.operator !== "all") params.set("operator", filters.operator)
      if (filters.sendVia && filters.sendVia !== "all") params.set("sendVia", filters.sendVia)
      if (filters.search) params.set("search", filters.search)
      if (filters.numberSearch) params.set("numberSearch", filters.numberSearch)
      if (filters.userId) params.set("userId", filters.userId)

      const response = await fetch(`${API_BASE_URL}/api/reports/campaigns/export?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.ok) {
        if (format === "excel") {
          const blob = await response.blob()
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement("a")
          a.href = url
          a.download = `campaign_report_filtered_${new Date().toISOString().split("T")[0]}.xlsx`
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          window.URL.revokeObjectURL(url)
          return { success: true }
        }
        return await response.json()
      }
      throw new Error("Export failed")
    } catch (error) {
      console.error("Error exporting filtered campaigns:", error)
      throw error
    }
  }


  const updateResellerBranding = async (brandingData) => {
    try {
      const response = await apiCall("/api/auth/reseller-branding", {
        method: "PUT",
        body: JSON.stringify(brandingData)
      })
      if (response.success && response.resellerBranding) {
        const updatedUser = {
          ...user,
          resellerBranding: response.resellerBranding
        }
        setUser(updatedUser)
        updateStoredAuthUser(updatedUser)
      }
      return response
    } catch (err) {
      console.error("Error updating reseller branding:", err)
      throw err
    }
  }

  const uploadResellerLogo = async (file, targetUserId = null) => {
    try {
      const formData = new FormData()
      formData.append("logo", file)
      if (targetUserId) formData.append("userId", targetUserId)

      const response = await apiCall(`/api/auth/upload-reseller-logo${targetUserId ? `?userId=${targetUserId}` : ""}`, {
        method: "POST",
        body: formData
      })
      if (response.success && response.logo && (!targetUserId || targetUserId === (user?.id || user?._id))) {
        const updatedBranding = response.resellerBranding || { ...(user?.resellerBranding || {}), logo: response.logo }
        const updatedUser = {
          ...user,
          resellerBranding: updatedBranding
        }
        setUser(updatedUser)
        updateStoredAuthUser(updatedUser)
      }
      return response
    } catch (err) {
      console.error("Error uploading reseller logo:", err)
      throw err
    }
  }

  const getUserChats = async (userId) => {
    try {
      const data = await apiCall(`/chats/${userId}`)
      return data
    } catch (error) {
      console.error("Error fetching user chats:", error)
      return { success: false, data: [] }
    }
  }

  const getChatMessages = async (userId, contactNumber) => {
    try {
      const data = await apiCall(`/chats/${userId}/${contactNumber}`)
      return data
    } catch (error) {
      console.error("Error fetching chat messages:", error)
      return { success: false, data: [] }
    }
  }

  const assignChatToAgent = async (userId, contactNumber, agentData) => {
    try {
      const data = await apiCall(`/chats/${userId}/${contactNumber}/assign`, {
        method: "PATCH",
        body: JSON.stringify(agentData),
      })
      return data
    } catch (error) {
      console.error("Error assigning chat:", error)
      return { success: false, error: error.message }
    }
  }

  const sendMessage = async (messageData) => {
    try {
      const data = await apiCall("/send-template", {
        method: "POST",
        body: JSON.stringify(messageData),
      })
      return data
    } catch (error) {
      console.error("Error sending message:", error)
      return { success: false, error: error.message }
    }
  }

  const getUserConversations = async (userId) => {
    try {
      const data = await apiCall(`/conversations/user/${userId}/conversations`)
      return data
    } catch (error) {
      console.error("Error fetching conversations:", error)
      return { success: false, data: [] }
    }
  }

  const getConversationMessages = async (userId, contactId) => {
    try {
      const data = await apiCall(`/conversations/user/${userId}/conversation/${contactId}/messages`)
      return data
    } catch (error) {
      console.error("Error fetching conversation messages:", error)
      return { success: false, data: [] }
    }
  }

  const uploadMedia1 = async (formData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/upload`, {
        method: "POST",
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: formData,
      })
      const data = await response.json()
      return data
    } catch (error) {
      console.error("Error uploading media:", error)
      return { success: false, error: error.message }
    }
  }

  const sendTemplateMessage = async (templateData) => {
    try {
      const data = await apiCall("/send-template", {
        method: "POST",
        body: JSON.stringify(templateData),
      })
      return data
    } catch (error) {
      console.error("Error sending template message:", error)
      return { success: false, error: error.message }
    }
  }

  const getUserMessages = async (params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString()
      const data = await apiCall(`/messages${queryParams ? `?${queryParams}` : ""}`)
      return data
    } catch (error) {
      console.error("Error fetching user messages:", error)
      return { success: false, messages: [] }
    }
  }

  const getMessageStats = async (params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString()
      const data = await apiCall(`/stats${queryParams ? `?${queryParams}` : ""}`)
      return data
    } catch (error) {
      console.error("Error fetching message stats:", error)
      return { success: false, stats: {} }
    }
  }

  const fetchContacts1 = async () => {
    try {
      const data = await apiCall("/contacts")
      return data.contacts || data.data || []
    } catch (error) {
      console.error("Error fetching contacts:", error)
      return []
    }
  }


  const value = {
    user,
    currentUser: user,
    token,
    loading,
    isAuthenticated: !!user && !!token,
    login,
    logout,
    verifyTwoFactorLogin,
    forgotPassword,
    verifyResetCode,
    resetPassword,
    getUserProfile,
    updateUserProfile,
    sendVerificationEmail,
    verifyEmail,
    addIPToWhitelist,
    removeIPFromWhitelist,
    toggleIPWhitelistStatus,
    toggleUserIPWhitelist,
    updateUserSecuritySettings,
    startTwoFactorSetup,
    confirmTwoFactorSetup,
    addIPToBlocklist,
    removeIPFromBlocklist,
    toggleIPBlocklistStatus,
    toggleUserIPBlocklist,
    regenerateApiKey,
    getUserApiKey,
    canEditProfile,
    canEditWhatsAppCredentials,
    canViewProfile,
    canManageUser,
    canManagePricing,
    canTransferBalance,
    canViewAllTransactions,
    getUserBalance,
    addBalance,
    removeBalance,
    transferBalance,
    syncUserLimit,
    getSupportNotifications,
    markSupportNotificationsRead,
    publicBranding,
    effectiveLogo,
    effectiveBrandName,
    updateResellerBranding,
    uploadResellerLogo,
    fetchReportStats,
    fetchCampaigns,
    fetchCampaignMessages,
    fetchCampaignConversationalReport,
    fetchRecipientConversationHistory,
    exportCampaignConversationalReport,
    exportCampaignReport,
    exportFilteredCampaigns,
    getUserChats,
    getChatMessages,
    assignChatToAgent,
    sendMessage,
    getUserConversations,
    getConversationMessages,
    uploadMedia1,
    sendTemplateMessage,
    getUserMessages,
    getMessageStats,
    fetchContacts,
    fetchContacts1,
    getAllUsers,
    fetchAllUsers,
    getQueueStats,
    getAllRecipients,
    makeDefaultPhone,
    apiCall,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
